/*
 * Image Mode Toolbar - Floating toolbar to change image mode (normal/wide/full)
 *
 * Requirements:
 * 1. Show when image is focused/selected
 * 2. Center horizontally on image (always)
 * 3. Use floating-ui for vertical positioning (top/bottom)
 * 4. Stay centered when mode changes (normal/wide/full)
 * 5. Use data-image-node attribute (not prosemirror classes) for querySelector
 */

import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
  type ReferenceElement,
} from '@floating-ui/dom'
import { Button } from '@blogging/ui'
import { isNodeSelection, useCurrentEditor } from '@tiptap/react'
import { useEffect, useRef, useState } from 'react'

export const ImageModeToolbar = () => {
  const { editor } = useCurrentEditor()
  const [isVisible, setIsVisible] = useState(false)
  const [currentMode, setCurrentMode] = useState<'normal' | 'wide' | 'full'>(
    'normal',
  )
  const [position, setPosition] = useState<{
    x: number
    y: number
    strategy: 'absolute' | 'fixed'
  } | null>(null)
  const toolbarRef = useRef<HTMLDivElement>(null)
  const cleanupRef = useRef<(() => void) | null>(null)
  const centerXRef = useRef<number | null>(null) // Store the center X position
  const currentImageRef = useRef<HTMLElement | null>(null) // Track which image we're tracking
  const lastModeRef = useRef<string | null>(null) // Track last mode to detect changes

  useEffect(() => {
    if (!editor) return

    const findImageElement = (): HTMLElement | null => {
      // Method 1: Use editor view to get DOM node directly (most reliable)
      // The new component-based image extension uses NodeViewWrapper with data-image-node="true"
      try {
        const { selection } = editor.state
        // For node selections (like images), check if it's a NodeSelection
        if (isNodeSelection(selection)) {
          const node = selection.node
          if (node && node.type.name === 'image') {
            const domNode = editor.view.nodeDOM(selection.from)
            // NodeViewWrapper is the wrapper div with data-image-node="true"
            if (
              domNode instanceof HTMLElement &&
              domNode.hasAttribute('data-image-node')
            ) {
              return domNode
            }
            // If it's a different structure, try to find the wrapper
            if (domNode instanceof HTMLElement) {
              const wrapper = domNode.querySelector('[data-image-node="true"]')
              if (wrapper instanceof HTMLElement) {
                return wrapper
              }
            }
          }
        } else if (!selection.empty) {
          // For other selections, try to get node at selection.from
          const node = editor.state.doc.nodeAt(selection.from)
          if (node && node.type.name === 'image') {
            const domNode = editor.view.nodeDOM(selection.from)
            // NodeViewWrapper is the wrapper div with data-image-node="true"
            if (
              domNode instanceof HTMLElement &&
              domNode.hasAttribute('data-image-node')
            ) {
              return domNode
            }
            // If it's a different structure, try to find the wrapper
            if (domNode instanceof HTMLElement) {
              const wrapper = domNode.querySelector('[data-image-node="true"]')
              if (wrapper instanceof HTMLElement) {
                return wrapper
              }
            }
          }
        }
      } catch (e) {
        // Fall through to querySelector
      }

      // Method 2: Find image wrapper using data-image-node attribute with selected class
      // The NodeViewWrapper has data-image-node="true" and gets ProseMirror-selectednode class
      let imageElement = document.querySelector(
        '[data-image-node="true"].ProseMirror-selectednode',
      ) as HTMLElement

      if (!imageElement) {
        imageElement = document.querySelector(
          '.ProseMirror-selectednode[data-image-node="true"]',
        ) as HTMLElement
      }

      // Method 3: Find all image wrappers and check which one has selected class
      if (!imageElement) {
        const allImageWrappers = document.querySelectorAll(
          '[data-image-node="true"]',
        )
        for (const wrapper of allImageWrappers) {
          if (wrapper.classList.contains('ProseMirror-selectednode')) {
            return wrapper as HTMLElement
          }
        }
      }

      return imageElement
    }

    const setupToolbar = () => {
      const isImageSelected = editor.isActive('image')

      if (!isImageSelected) {
        setIsVisible(false)
        setPosition(null)
        setCurrentMode('normal')
        centerXRef.current = null
        currentImageRef.current = null
        lastModeRef.current = null
        if (cleanupRef.current) {
          cleanupRef.current()
          cleanupRef.current = null
        }
        return
      }

      // Update current mode from editor attributes
      const mode = editor.getAttributes('image').mode || 'normal'
      setCurrentMode(mode as 'normal' | 'wide' | 'full')

      // Wait for DOM to update
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const imageElement = findImageElement()

          if (!imageElement) {
            // Retry with delay if not found
            setTimeout(() => {
              const retryElement = findImageElement()
              if (!retryElement) {
                setIsVisible(false)
                setPosition(null)
                return
              }
              setupToolbarWithElement(retryElement)
            }, 150)
            return
          }

          if (!toolbarRef.current) {
            // Toolbar ref not ready yet, retry
            setTimeout(() => {
              if (toolbarRef.current) {
                setupToolbarWithElement(imageElement)
              }
            }, 50)
            return
          }

          setupToolbarWithElement(imageElement)
        })
      })
    }

    const setupToolbarWithElement = (imageElement: HTMLElement) => {
      if (!toolbarRef.current) {
        setIsVisible(false)
        setPosition(null)
        return
      }
      setIsVisible(true)

      // Get current mode to detect mode changes
      const mode = editor.getAttributes('image').mode || 'normal'

      // Calculate and store the image's center X position (in viewport coordinates)
      // Recalculate if it's a different image OR if the mode changed (image size changes with mode)
      const isNewImage = currentImageRef.current !== imageElement
      const modeChanged = lastModeRef.current !== mode

      if (isNewImage || modeChanged) {
        // Recalculate center when image or mode changes
        const imageRect = imageElement.getBoundingClientRect()
        centerXRef.current = imageRect.x + imageRect.width / 2
        currentImageRef.current = imageElement
        lastModeRef.current = mode
      }

      // Create reference that always gets fresh bounding rect
      const virtualReference: ReferenceElement = {
        getBoundingClientRect: () => imageElement.getBoundingClientRect(),
      }

      // Update position function - uses stored center X, only updates Y
      const updatePosition = async () => {
        if (!toolbarRef.current || !imageElement || centerXRef.current === null)
          return

        try {
          // Only calculate Y position, use stored center X for horizontal positioning
          const { y, strategy } = await computePosition(
            virtualReference,
            toolbarRef.current,
            {
              placement: 'top',
              strategy: 'fixed',
              middleware: [offset(8), flip(), shift({ padding: 8 })],
            },
          )

          // Calculate toolbar X position: center it on the stored image center X
          // This ensures horizontal position stays fixed even when image mode changes
          const toolbarRect = toolbarRef.current.getBoundingClientRect()
          const toolbarX = centerXRef.current - toolbarRect.width / 2

          setPosition({
            x: toolbarX,
            y,
            strategy,
          })
        } catch (error) {
          // Ignore errors
        }
      }

      // Initial position
      updatePosition()

      // Clean up previous autoUpdate
      if (cleanupRef.current) {
        cleanupRef.current()
      }

      // Auto-update position (handles scroll, resize, and mode changes)
      cleanupRef.current = autoUpdate(
        virtualReference,
        toolbarRef.current,
        updatePosition,
      )
    }

    // Update on selection and transaction changes
    editor.on('selectionUpdate', setupToolbar)
    editor.on('transaction', setupToolbar)

    // Initial setup
    setupToolbar()

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current()
      }
      editor.off('selectionUpdate', setupToolbar)
      editor.off('transaction', setupToolbar)
    }
  }, [editor])

  // Always render the toolbar div (even if hidden) so we can debug
  // Only hide it if editor is not available
  if (!editor) return null

  const setImageMode = (mode: 'normal' | 'wide' | 'full') => {
    if (!editor) return

    const currentAttrs = editor.getAttributes('image')
    const selection = editor.state.selection

    // Update image mode
    editor.commands.updateAttributes('image', {
      ...currentAttrs,
      mode,
    })

    // Update local state immediately for responsive UI
    setCurrentMode(mode)

    // Restore selection to keep image selected
    requestAnimationFrame(() => {
      if (editor && !editor.isDestroyed) {
        try {
          editor.commands.setNodeSelection(selection.from)
        } catch (e) {
          // Ignore errors
        }
      }
    })
  }

  return (
    <div
      ref={toolbarRef}
      style={{
        position: position?.strategy || 'fixed',
        left: position ? `${position.x}px` : '-9999px',
        top: position ? `${position.y}px` : '-9999px',
        zIndex: 1000,
        visibility: isVisible && position ? 'visible' : 'hidden',
        pointerEvents: isVisible && position ? 'auto' : 'none',
      }}
      className="flex gap-1 rounded-md border bg-background p-1 shadow-md"
      onMouseDown={(e) => {
        e.stopPropagation()
        e.preventDefault()
      }}
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
      }}
    >
      <Button
        variant={currentMode === 'normal' ? 'default' : 'ghost'}
        size="sm"
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          setImageMode('normal')
        }}
        onMouseDown={(e) => {
          e.stopPropagation()
          e.preventDefault()
        }}
        className="h-7 px-2 text-xs"
      >
        Normal
      </Button>
      <Button
        variant={currentMode === 'wide' ? 'default' : 'ghost'}
        size="sm"
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          setImageMode('wide')
        }}
        onMouseDown={(e) => {
          e.stopPropagation()
          e.preventDefault()
        }}
        className="h-7 px-2 text-xs"
      >
        Wide
      </Button>
      <Button
        variant={currentMode === 'full' ? 'default' : 'ghost'}
        size="sm"
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          setImageMode('full')
        }}
        onMouseDown={(e) => {
          e.stopPropagation()
          e.preventDefault()
        }}
        className="h-7 px-2 text-xs"
      >
        Full
      </Button>
    </div>
  )
}
